import { useGetPresignedUrlMutation, useUploadFileToS3Mutation, useGetAdvisorKnowledgeDetailsQuery } from "@circuitry-ai/doc-data";
import { CtyShowToast, checkType } from "@circuitry-ai/doc-ui";
import { Icon } from "@iconify/react";
import { Card, Progress } from "@nextui-org/react";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

interface FileList {
    size: string;
    name: string;
    type: string;
    startTraining: boolean;
    uploading: boolean;
    fileUploaded: boolean;
    source_id?: string;
}

interface KnowledgeFileUploadProps {
    advisorID: string;
    onUploadComplete?: () => void;
    uploadAreaId?: string;
}

function formatFileSize(bytes: number) {
    const KB = 1024;
    const MB = KB * 1024;

    if (bytes >= MB) {
        return (bytes / MB).toFixed(2) + ' MB';
    } else {
        return (bytes / KB).toFixed(2) + ' KB';
    }
}

const updateFileStatus = (prev: FileList[], fileName: string, updates: Partial<FileList>) => {
    const updatedFiles = [...prev];
    const fileIndex = updatedFiles.findIndex(f => f.name === fileName);
    if (fileIndex !== -1) {
        updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], ...updates };
    }
    return updatedFiles;
};

const KnowledgeFileUpload = (props: KnowledgeFileUploadProps) => {
    const { advisorID, onUploadComplete, uploadAreaId } = props;
    const { t } = useTranslation();

    const [filesList, setFilesList] = useState<FileList[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    const [getPresignedUrl] = useGetPresignedUrlMutation();
    const [uploadFileTsetShowPdfoS3] = useUploadFileToS3Mutation();
    const { refetch } = useGetAdvisorKnowledgeDetailsQuery({
        advisorId: advisorID,
        pagination: { page: 1, page_size: 50 },
        column_filters: [],
        metadata_filters: [],
    });

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            uploadFiles(Array.from(files));
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            uploadFiles(Array.from(e.target.files));
        }
    };

    const getFileType = (filename: string) => {
        const name = filename.split('.');
        return name[name.length - 1];
    }

    const uploadFiles = (files: any[]) => {
        const uniqueNewFiles = files.filter((file) => {
            return !filesList.some(existingFile => existingFile.name === file.name);
        });

        if (uniqueNewFiles.length !== files.length) {
            CtyShowToast(checkType.error, 'Some files have duplicate filenames and were skipped.');
        }

        if (uniqueNewFiles.length === 0) return;

        const currentFiles = uniqueNewFiles.map((file) => ({
            size: formatFileSize(file.size),
            name: file.name,
            type: getFileType(file.name).toUpperCase(),
            startTraining: false,
            uploading: true,
            fileUploaded: false,
        }));

        const uploadingFiles = [...filesList, ...currentFiles];
        setFilesList(uploadingFiles);

        const uploadSingleFile = async (file: FileList, index: number) => {
            try {
                const response = await getPresignedUrl({
                    advisor_id: advisorID,
                    data: [{ filename: file.name, parts: 0 }],
                }).unwrap();
                const presignedUrl = response.data?.[0].presigned_urls.part_1;
                await uploadFileTsetShowPdfoS3({
                    file: uniqueNewFiles[index],
                    presignedUrl,
                    CtyShowToast,
                }).unwrap();
                setFilesList((prev) => updateFileStatus(prev, file.name, {
                    uploading: false,
                    fileUploaded: true,
                    startTraining: true,
                }));
                refetch();
                onUploadComplete?.();
            } catch (error: unknown) {
                setFilesList((prev) => updateFileStatus(prev, file.name, {
                    uploading: false,
                    fileUploaded: false,
                }));
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                CtyShowToast(checkType.error, `Failed to upload ${file.name}: ${errorMessage}`);
            }
        };

        for (const [index, file] of currentFiles.entries()) {
            uploadSingleFile(file, index);
        }
    };


    function getFileStatus(file: FileList) {
        if (file.uploading) {
            return 'Uploading...';
        } else if (file.fileUploaded) {
            return '100%';
        } else {
            return '';
        }
    }

    const renderFilesList = () => {
        return (
            <div className="flex flex-col  gap-3  g-pink-100">
                {filesList.map((file, index) => {
                    return (
                        <div key={index + 'upload-file'} className="flex items-center justify-between p-3 pl-2 gap-3 border-1 rounded-md">
                            <div className="flex flex-row items-center gap-2 w-full bgd-green-100 h-[60px]">
                                <div className="relative h-full">
                                    <Icon icon="circum:file-on" width="4em" height="4em" className="text-[#D5D7DA] dark:text-default-500" />
                                    <span className="text-[11px] bg-danger px-[6px] py-[1px] absolute bottom-3 left-1 rounded-sm text-[#fff]">{file.type}</span>
                                </div>
                                <div className="flex flex-col justify-around bgd-yellow-200 h-full w-full">
                                    <p className="font-medium text-sm">{file.name}</p>
                                    <p className="text-sm text-default-400 font-normal">{file.size}</p>
                                    <Progress
                                        aria-label="Loading..."
                                        size="sm"
                                        value={file.fileUploaded ? 100 : undefined}
                                        radius="md"
                                        isIndeterminate={file.uploading}
                                    />
                                </div>
                                <div className={`h-full flex flex-col justify-end`}>
                                    <div className="text-sm">{getFileStatus(file)}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <Card className="h-[160px] justify-center border-2 border-primary shadow-md">
                <button
                    type="button"
                    id={uploadAreaId || advisorID + '-advisor-knowledgeupload'}
                    className="w-full h-full"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                >
                    <div className="h-full flex flex-col items-center justify-center">
                        <div className="border-2 border-dotted border-default-300 p-2 rounded-lg">
                            <Icon icon="icon-park-outline:upload-one" width="22" height="22" className="text-foreground" />
                        </div>
                        <div className="text-center">
                            <span className="flex py-2">
                                <p className="font-semibold text-md pr-2 text-primary">
                                    {t('Click to upload')}
                                </p>
                                <p>{t('or drag and drop')}</p>
                            </span>
                            <div className="text-sm">
                                {t('PDF, MP4, MP3 or CSV (max. 800x400px)')}
                            </div>
                        </div>
                        <div className="mt-2 flex justify-center space-x-4 mb-2">
                            <input
                                id="file-input"
                                ref={inputRef}
                                type="file"
                                multiple
                                onChange={handleFileInputChange}
                                className="hidden"
                            />
                        </div>
                    </div>
                </button>
            </Card>
            <div className="flex-1 overflow-y-auto">
                {renderFilesList()}
            </div>
        </>
    );
};

export { KnowledgeFileUpload };

